import { useCallback, useEffect, useRef } from 'react';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	useUFOExperience,
} from '@atlassian/ufo-experimental';

import type { AudioFormat } from '../../api/cc-smarts/getAudio';
import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import {
	type AudioPlayerStreamingAction,
	AudioPlayerStreamingManager,
} from '../effects/AudioPlayerStreamingManager';
import { getAIFeatureName } from '../effects/getAIFeatureName';
import { getRequestParamsForAudioPlayer } from '../effects/getRequestParamsForAudioPlayer';
import { getSupportingContent } from '../state/AudioPlayerState';
import { AudioStreamingState } from '../state/AudioPlayerStreamingStore';

import { useAudioPlayerStore } from './useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from './useAudioPlayerStreamingStore';

type AudioPlayerStreamingConfig = {
	locale: string;
	format: AudioFormat;
	activationId: string;
	product: string;
	cloudId: string;
};

export function useAudioPlayerStreaming({
	activationId,
	product,
	cloudId,
	locale,
	format,
}: AudioPlayerStreamingConfig) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const audioPlayerStreamingManager = useRef<AudioPlayerStreamingManager | null>(null);
	const featureOverrideRef = useRef<AudioFeatureType | null>(null);

	const { state, updateAudioPlayerStreamingStore } = useAudioPlayerStreamingStore();
	const { activeFeature } = useAudioPlayerStore();
	const { start, success, failure } = useUFOExperience({
		id: 'audio-player-streaming',
		component: 'audio-player',
		type: ExperienceTypes.Experience,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	const { trackAIInteractionInit, trackAIResultView, trackAIResultError, updateAIAttributes } =
		useAIEventsInstrumentation();

	const handleObservabilityAndAnalytics = useCallback(
		async (action: AudioPlayerStreamingAction) => {
			const featureToUse = featureOverrideRef.current ?? activeFeature;

			switch (action.type) {
				case AudioStreamingState.LOADING:
					const featureName = getAIFeatureName(featureToUse);
					// Update AI analytics config
					updateAIAttributes({ aiFeatureName: featureName });
					// Ensure the new AI experience is started with the correct activeFeature (not stale)
					trackAIInteractionInit({
						attributes: { aiFeatureName: featureName },
					});
					await start();
					break;
				case AudioStreamingState.STREAM_DISPOSED:
					trackAIResultView();
					await success();
					break;
				case AudioStreamingState.ERROR:
					trackAIResultError({
						aiErrorMessage: action.error.message,
					});
					await failure({
						metadata: {
							error: action.error.message,
						},
					});
					break;
			}
		},
		[
			trackAIInteractionInit,
			start,
			trackAIResultView,
			success,
			trackAIResultError,
			failure,
			activeFeature,
			updateAIAttributes,
		],
	);

	const onAudioStreamingChange = useCallback(
		(action: AudioPlayerStreamingAction) => {
			updateAudioPlayerStreamingStore(action);
			handleObservabilityAndAnalytics(action);
		},
		[updateAudioPlayerStreamingStore, handleObservabilityAndAnalytics],
	);

	const fetchAudio = useCallback(
		async (featureOverride?: AudioFeatureType) => {
			featureOverrideRef.current = featureOverride ?? null;

			// Dispose previous player and stream if exists
			audioPlayerStreamingManager.current?.pauseAndResetPlayer();
			audioPlayerStreamingManager.current?.disposeStream();

			// Get the latest supporting content from AudioPlayerState
			const supportingContent = getSupportingContent();
			const overrideSupportingContent =
				supportingContent.length > 0
					? supportingContent.map((resource) => resource.ari)
					: undefined;

			const requestParams = getRequestParamsForAudioPlayer({
				activationId,
				product,
				cloudId,
				locale,
				format,
				overrideSupportingContent,
			});

			audioPlayerStreamingManager.current = new AudioPlayerStreamingManager({
				requestParams,
				onAudioStreamingChange,
			});

			audioPlayerStreamingManager.current.validateRequestParams();
			await audioPlayerStreamingManager.current.fetchAudio();
			audioPlayerStreamingManager.current.streamAudioToMediaSource(audioRef.current);
		},
		[activationId, product, cloudId, locale, format, onAudioStreamingChange],
	);

	useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			audioPlayerStreamingManager.current?.disposeStream();
			audioPlayerStreamingManager.current?.resetState();
		};
	}, []);

	return { state, fetchAudio, audioRef };
}
